import {
  DialogTitle,
  IconButton,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Typography,
  Stack,
  Tooltip,
  Chip,
  Dialog
} from '@mui/material'
import { Circle, CircleHalfFull, Close, MenuDown } from 'mdi-material-ui'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { styled } from '@mui/material/styles'
import { Variation } from '@/@core/services/app.service'
import { ColumnDef } from '@tanstack/react-table'
import { formatVariantInheritance } from '@/@core/utils/utils'
import Slide from '@mui/material/Slide'
import TanStackTable from '@/@core/components/tanstack-tables'

import CloseIcon from '@mui/icons-material/Close'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import _ from 'lodash'
import { TransitionProps } from '@mui/material/transitions'
import { OMIM } from '../../OMIM'

const TypographyA = styled(Typography)(() => ({
  fontSize: '0.75rem',
  color: 'inherit'
}))

const color = [
  {
    key: 'pathogenic',
    value: 'pathogenic',
    color: 'rgba(229,38,35,0.8)'
  },
  {
    key: 'likely pathogenic',
    value: 'likely pathogenic',
    color: 'rgba(221,109,32,0.8)'
  },
  {
    key: 'drug response',
    value: 'drug response',
    color: 'rgb(46, 125, 50,0.8)'
  },
  {
    key: 'uncertain significance',
    value: 'uncertain significance',
    color: 'rgb(240,239,240,0.4)'
  },
  {
    key: 'likely benign',
    value: 'likely benign',
    color: 'rgb(107,148,200,0.8)'
  },
  {
    key: 'benign',
    value: 'benign',
    color: 'rgb(109,56,149,0.8)'
  },
  {
    key: 'loss of function',
    value: 'loss of function',
    color: 'rgb(237, 108, 2,0.8)'
  }
]

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />
})

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiPaper-root': {
    maxWidth: 'none'
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2)
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1)
  }
}))

const SampleInfo = (props: any) => {
  const { t } = useTranslation()

  //

  const pagination = {
    pageIndex: 0,
    pageSize: 30
  }
  const [tKey, setTKey] = React.useState(0)
  const [page, setPage] = React.useState(pagination)
  const [sortQuery, setSortQuery] = React.useState<any>({
    f: -1
  })
  const [current, setCurrent] = React.useState<Variation | null>(null)
  const [OMIMOpen, setOMIMOpen] = React.useState(false)
  const [variantList, setVariantList] = React.useState<any[]>([])
  const tableRef = React.useRef<any>()
  const [backop, setBackop] = React.useState(true)

  const changeOMIMChecked = (value: boolean, _id: string, phenoMim: string) => {
    const a = tableRef.current.dataMemo

    const temp = _.cloneDeep(a)
    const flag = temp.find((items: any) => items._id === _id)

    if (flag && flag.OMIM && flag.OMIM.omimData) {
      const omimData = flag.OMIM.omimData
      const find = omimData.find((item: any) => item.phenoMim === phenoMim)

      if (find) {
        find.isCheck = value

        setTKey(Math.random())

        setVariantList(temp)
      }
    }
  }

  React.useEffect(() => {
    setVariantList(props.data.samples_info ?? [])
  }, [props.data])

  const defaultColumns: ColumnDef<any>[] = React.useMemo(
    () => [
      {
        accessorKey: 'SampleId',
        id: 'SampleId',
        header: () => (
          <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
            <Stack maxWidth={'100%'}>
              <span
                style={{
                  lineHeight: 'normal',
                  textAlign: 'left'
                }}
              >
                {`${t('Sample')}Id`}
              </span>
            </Stack>
          </Stack>
        ),
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => (
          <Stack maxWidth={'100%'}>
            <Stack maxWidth={'100%'} direction="column" spacing={1}>
              <TypographyA
                sx={{
                  textAlign: 'left '
                }}
              >
                {row.original.sample_id}
              </TypographyA>
            </Stack>
          </Stack>
        )
      },
      {
        accessorKey: 'coverage',
        id: 'coverage',
        enableSorting: false,
        header: () => (
          <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
            <Tooltip
              placement="top-start"
              title={`${t(
                'Solid round surface homozygous, semi-solid surface heterozygous'
              )}；ref/alt`}
            >
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {`${t('Zygosity')}/${t('Coverage')}`}
                </span>
              </Stack>
            </Tooltip>
          </Stack>
        ),
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => {
          let bgcolor = color.find(
            (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
          )
            ? (color.find(
                (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
              )?.color as any)
            : 'rgba(0, 0, 0)'

          // loss_of_function等于1 并且CLINSIG_final不等于P/LP
          if (row.original.CLINSIG_final?.toLowerCase() === 'pathogenic') {
            //
          } else if (
            row.original.CLINSIG_final?.toLowerCase() === 'likely pathogenic'
          ) {
            //
          } else if (row.original.loss_of_function === 1) bgcolor = '#f5d18c'

          return (
            <Stack maxWidth={'100%'} direction="column" spacing={1}>
              <React.Fragment>
                <Stack direction={'row'} alignItems="center" spacing={1}>
                  <Stack maxWidth={'100%'} direction="column" spacing={1}>
                    {row.original.allele_frequency < 0.85 ? (
                      <CircleHalfFull
                        sx={{ color: 'black', fontSize: '0.875rem' }}
                      />
                    ) : (
                      <Circle sx={{ color: 'black', fontSize: '0.875rem' }} />
                    )}
                  </Stack>

                  <TypographyA
                    sx={{
                      maxWidth: '100%'
                    }}
                  >
                    {`${getValue<string>()}  [${row.original.allele_frequency?.toFixed(
                      2
                    )}] `}
                  </TypographyA>
                </Stack>
              </React.Fragment>
            </Stack>
          )
        }
      },
      {
        accessorKey: 'f_score',
        id: 'F_score',
        header: () => (
          <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
            <Stack maxWidth={'100%'}>
              <span
                style={{
                  lineHeight: 'normal',
                  textAlign: 'left'
                }}
              >
                {`F Score`}
              </span>
            </Stack>
          </Stack>
        ),
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => {
          let bgcolor = color.find(
            (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
          )
            ? (color.find(
                (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
              )?.color as any)
            : 'rgba(0, 0, 0)'

          // loss_of_function等于1 并且CLINSIG_final不等于P/LP
          if (row.original.CLINSIG_final?.toLowerCase() === 'pathogenic') {
            //
          } else if (
            row.original.CLINSIG_final?.toLowerCase() === 'likely pathogenic'
          ) {
            //
          } else if (row.original.loss_of_function === 1) bgcolor = '#f5d18c'

          return (
            <Stack maxWidth={'100%'}>
              <Stack maxWidth={'100%'} direction="column" spacing={1}>
                <React.Fragment>
                  <TypographyA
                    onClick={(e) => {
                      e.stopPropagation()

                      setCurrent(row.original)
                    }}
                    sx={{
                      cursor: 'pointer',
                      fontSize: '0.8rem',
                      color:
                        bgcolor === 'rgba(229,38,35,0.8)'
                          ? 'white'
                          : bgcolor === 'rgb(107,148,200,0.8)' ||
                            bgcolor === 'rgb(109,56,149,0.8)'
                          ? 'black'
                          : '#23527c',
                      textAlign: 'left',
                      fontWeight: '700'
                    }}
                  >
                    {`${
                      (row.original.f_score as any) !== '.'
                        ? (row.original.f_score * 100)?.toFixed(2)
                        : '.'
                    }`}
                  </TypographyA>
                </React.Fragment>
              </Stack>
            </Stack>
          )
        }
      }
    ],
    [variantList, changeOMIMChecked]
  )

  return (
    <Box minWidth={1200}>
      <Grid container width={'100%'} height={'100%'} spacing={4}>
        <Grid item xs={12}>
          <Stack spacing={6} width="100%">
            <TanStackTable
              columns={defaultColumns as any}
              data={variantList}
              pagination={{
                pageIndex: 1,
                pageSize: 30
              }}
              pageCount={variantList.length}
              setRowSelection={(rowSelection: number[]) => {
                //
              }}
              setPagination={setPage}
              setSortQuery={setSortQuery}
              sorting={[]}
              isFetching={false}
              setPage={null}
              variantCount={0}
              totalCount={0}
              ref={tableRef}
              disabledExpand={true}
              analysisInfo={props.analysisInfo}
            />
          </Stack>
        </Grid>
      </Grid>

      {/* OMIM */}

      <BootstrapDialog
        onClose={() => setOMIMOpen(!OMIMOpen)}
        aria-labelledby="customized-dialog-title"
        open={OMIMOpen}
        TransitionComponent={Transition}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          {`${t('Clinical Synopsis')}(Gene:${current?.gene})`}
        </DialogTitle>

        <IconButton
          aria-label="close"
          onClick={() => setOMIMOpen(!OMIMOpen)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent dividers sx={{ maxWidth: '100%', minHeight: 300 }}>
          <Box height={'100%'} width={'100%'}>
            <OMIM
              params={current}
              setBackop={setBackop}
              analysisInfo={props.analysisInfo}
            />
          </Box>
        </DialogContent>
      </BootstrapDialog>
    </Box>
  )
}
export default SampleInfo
